import axios from 'axios'
import { useMemo, useState } from 'react'

import { Pulse } from '@/components/activity/activity-panel'
import { loginAsPurposeItems } from '@/components/login-as-user-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import type { LoginAsAuditEvent } from '@/models/IActivity'
import { useInfiniteQueryGetOrgLoginAsAudit } from '@/services'
import { formatAuditTime } from '@/utils/activity'

const PURPOSE_TITLES: Record<string, string> = Object.fromEntries(
  loginAsPurposeItems.map((item) => [item.value, item.title])
)

const getPurposeTitle = (name?: string) =>
  (name && PURPOSE_TITLES[name]) || name || 'No purpose given'

const getErrorText = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 404)
      return 'The login-as audit endpoint is not available on this stage yet.'
    if (error.response?.status === 401 || error.response?.status === 403)
      return 'The user API refused the request.'
    if (!error.response) return 'The user API could not be reached.'
  }

  return 'The login-as audit log could not be loaded.'
}

const matches = (event: LoginAsAuditEvent, search: string) =>
  [
    event.admin_email,
    event.user_email,
    event.purpose?.name,
    getPurposeTitle(event.purpose?.name),
    event.purpose?.description
  ].some((value) => value?.toLowerCase().includes(search))

/** "login as" sessions epilot staff started in this organization and the purpose they gave */
export const LoginAsAuditTab = ({ orgId }: { orgId: string }) => {
  const [search, setSearch] = useState('')
  const {
    data,
    error,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useInfiniteQueryGetOrgLoginAsAudit(orgId)

  const events = useMemo(
    () => data?.pages.flatMap((page) => page.events) ?? [],
    [data]
  )
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()

    return term ? events.filter((event) => matches(event, term)) : events
  }, [events, search])

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          aria-label="Search login-as sessions"
          className="h-8 w-72"
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search admin, user or purpose"
          value={search}
        />
        <span className="text-xs text-muted-foreground">
          &quot;Login as&quot; sessions started by epilot staff in organization{' '}
          <span className="font-mono">{orgId}</span>, newest first
        </span>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }, (_, i) => (
            <Pulse className="h-10 w-full" key={i} />
          ))}
        </div>
      ) : error ? (
        <p className="rounded-md bg-muted/30 p-4 text-sm">
          {getErrorText(error)}
        </p>
      ) : filtered.length === 0 ? (
        <p className="rounded-md bg-muted/30 p-4 text-sm text-muted-foreground">
          {events.length === 0
            ? 'Nobody has logged in as a user of this organization yet.'
            : 'No sessions match the search.'}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">When</TableHead>
              <TableHead>epilot admin</TableHead>
              <TableHead>Logged in as</TableHead>
              <TableHead>Purpose</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((event) => (
              <TableRow key={event.id}>
                <TableCell className="whitespace-nowrap align-top tabular-nums">
                  <time
                    dateTime={event.created_at}
                    title={new Date(event.created_at).toLocaleString('de-DE')}
                  >
                    {formatAuditTime(event.created_at)}
                  </time>
                </TableCell>
                <TableCell className="align-top">
                  {event.admin_email ?? '—'}
                </TableCell>
                <TableCell className="align-top">
                  {event.user_email ?? '—'}
                </TableCell>
                <TableCell className="space-y-1 align-top">
                  <Badge variant="secondary">
                    {getPurposeTitle(event.purpose?.name)}
                  </Badge>
                  {event.purpose?.description && (
                    <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                      {event.purpose.description}
                    </p>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {hasNextPage && (
        <Button
          loading={isFetchingNextPage}
          onClick={() => fetchNextPage()}
          size="sm"
          variant="outline"
        >
          Load older sessions
        </Button>
      )}
    </div>
  )
}
